import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { SelectChangeEvent } from '@mui/material';

import { modalActions } from '@contexts/common/ModalContext';
import { useFactories } from '@contexts/vaults/VaultFactoriesContext';
import { useVaultTokens } from '@contexts/vaults/VaultTokensContext';
import {
  useVaultsFilters,
  vaultsFiltersActions,
} from '@contexts/vaults/VaultsFiltersContext';

import useDexFactories from '@hooks/vaults/useDexFactories';
import useTagsBadges from '@hooks/vaults/useTagsBadges';

import { toggleFilterState } from '@helpers/vaults/vault.helpers';

import { CHAIN_ID, CHAIN_INFO } from '@constants/common/chain.constants';
import { SortDestination } from '@constants/common/common.constants';
import {
  ALL_FARM_TYPES,
  FarmsFilterTab,
  RANGES,
  V3FarmsFilterTab,
} from '@constants/vaults/common.constants';

import { CommonTokenDto } from '@dto/common/tokens.dto';

interface ProfitToken {
  label: string;
  chainId: number;
  address: string;
}

const useAdvancedFilters = () => {
  const {
    tags: tagsValues,
    ranges: rangesValues,
    search,
    sortBy,
    sortDirection,
    riskLevelFrom,
    riskLevelTo,
    profitPercentFrom,
    profitPercentTo,
    selectedChains,
    platforms: platformValues,
    strategy: strategyValues,
    farmTypes: farmTypesValues,
    profitTokens: profitTokensValues,
    v3Type: v3TypeValues,
  } = useVaultsFilters();

  const { tokens, isLoading: isLoadingTokens } = useVaultTokens();
  const { vaultFactories, isLoading: isLoadingVaultFactories } = useFactories();
  const { badges, isLoading: isLoadingBadges } = useTagsBadges(selectedChains);
  const { dexFactories, isLoading: isLoadingDexFactories } =
    useDexFactories(selectedChains);

  const initialTags = useMemo(() => {
    if (!badges.length || isLoadingBadges || !tagsValues.length) return [];
    return badges.filter((badge) => tagsValues.includes(badge.value));
  }, [badges, isLoadingBadges, tagsValues]);

  const initialRanges = useMemo(() => {
    if (!rangesValues.length) return [];
    return RANGES.filter((range) => rangesValues.includes(range.value));
  }, [rangesValues]);

  const initialPlatforms = useMemo(() => {
    if (!platformValues.length) return [];
    const chainIds = platformValues.map(Number);
    return dexFactories.reduce<number[]>((acc, dex) => {
      if (chainIds.includes(dex.id)) {
        acc.push(dex.id);
      }
      return acc;
    }, []);
  }, [dexFactories, platformValues]);

  const [input, setInput] = useState(search);
  const [tags, setTags] = useState<{ label: string; value: string }[]>([]);
  const [ranges, setRanges] = useState<{ label: string; value: string }[]>([]);
  const [farmTypes, setFarmTypes] = useState(farmTypesValues);
  const [strategy, setStrategy] = useState<string | undefined>(strategyValues);
  const [v3Type, setV3Type] = useState<string | undefined>(v3TypeValues);
  const [platforms, setPlatform] = useState<number[]>(initialPlatforms);
  const [profitTokens, setProfitToken] = useState<string[]>(profitTokensValues);
  const [riskLevel, setRiskLevel] = useState([riskLevelFrom, riskLevelTo]);
  const [sort, setSort] = useState({ sortBy, sortDirection });
  const [profitLevel, setProfitLevel] = useState([
    profitPercentFrom,
    profitPercentTo,
  ]);

  const isGlobalLoading = useMemo(
    () =>
      isLoadingTokens ||
      isLoadingVaultFactories ||
      isLoadingBadges ||
      isLoadingDexFactories,
    [
      isLoadingBadges,
      isLoadingDexFactories,
      isLoadingTokens,
      isLoadingVaultFactories,
    ],
  );

  const disabledV3Type = useMemo(
    () => !!farmTypes.length && !farmTypes.includes(FarmsFilterTab.V3),
    [farmTypes],
  );

  const onSetProfitToken = (event: SelectChangeEvent<unknown>) => {
    setProfitToken(event.target.value as string[]);
  };

  const onSetPlatform = (event: SelectChangeEvent<unknown>) => {
    setPlatform(event.target.value as number[]);
  };

  const onInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  }, []);

  const onSortBy = useCallback((newSortBy: string) => {
    setSort((prevState) => ({
      ...prevState,
      sortBy: newSortBy,
    }));
  }, []);

  const onSortDirection = useCallback((newSortDirection: string) => {
    setSort((prevState) => ({
      ...prevState,
      sortDirection: newSortDirection as SortDestination,
    }));
  }, []);

  const onStrategy = useCallback((value: string | number) => {
    setStrategy(String(value));
  }, []);

  const onV3Type = useCallback((value: string | number) => {
    if (value === V3FarmsFilterTab.Ailm) {
      setRanges([]);
    }
    setV3Type(String(value));
  }, []);

  const onFarmTypes = useCallback((value: string | number) => {
    setFarmTypes((prevSelected) => {
      const shouldClear =
        (!prevSelected.includes(FarmsFilterTab.V3) &&
          value !== FarmsFilterTab.V3) ||
        (prevSelected.includes(FarmsFilterTab.V3) &&
          (value === FarmsFilterTab.V3 || value !== FarmsFilterTab.V3));
      if (shouldClear) {
        setRanges([]);
        setV3Type(undefined);
      }

      return toggleFilterState(prevSelected, String(value), ALL_FARM_TYPES);
    });
  }, []);

  const onRiskLevel = useCallback((_: Event, newValue: number | number[]) => {
    setRiskLevel(newValue as number[]);
  }, []);

  const onProfitLevel = useCallback((_: Event, newValue: number | number[]) => {
    setProfitLevel(newValue as number[]);
  }, []);

  const onTags = useCallback((item: { value: string; label: string }) => {
    setTags((prevTags) =>
      prevTags.some((tag) => tag.value === item.value)
        ? prevTags.filter((tag) => tag.value !== item.value)
        : [...prevTags, item],
    );
  }, []);

  const onRanges = useCallback((item: { value: string; label: string }) => {
    setRanges((prevItem) =>
      prevItem.some((tag) => tag.value === item.value)
        ? prevItem.filter((range) => range.value !== item.value)
        : [...prevItem, item],
    );
  }, []);

  const onClear = useCallback(() => {
    setTags([]);
  }, []);

  const profitTokensList: ProfitToken[] = useMemo(() => {
    if (!tokens) return [];

    const allowedEtfsSet = vaultFactories.reduce<Set<string>>(
      (acc, factory) => {
        if (selectedChains.includes(factory.chainId)) {
          factory.profitTokensWl.forEach((item) => acc.add(item));
        }
        return acc;
      },
      new Set(),
    );

    const allowedProfitTokens = Array.from(allowedEtfsSet);

    let profitTokensMap: Record<string, CommonTokenDto> = {};
    for (let i = 0; i < selectedChains.length; i++) {
      if (tokens.profit[selectedChains[i]]) {
        const chainProfitTokens = tokens.profit[selectedChains[i]];
        profitTokensMap = { ...profitTokensMap, ...chainProfitTokens };
      }
    }

    return allowedProfitTokens
      .map((item) => {
        const token = profitTokensMap[item];
        return token
          ? {
              label: token.symbol,
              chainId: token.chainId,
              address: token.address,
            }
          : null;
      })
      .filter((token): token is ProfitToken => token !== null);
  }, [selectedChains, tokens, vaultFactories]);

  const profitTokensOptions = useMemo(
    () =>
      profitTokensList.map((item) => ({
        name: item.label,
        value: `${item.chainId}_${item.address}`,
        subLabel: CHAIN_INFO[item.chainId as CHAIN_ID].name,
      })),
    [profitTokensList],
  );

  const platformOptions = useMemo(
    () =>
      dexFactories.length
        ? dexFactories.map((item) => ({
            name: item.name,
            value: item.id,
            subLabel: CHAIN_INFO[item.chainId as CHAIN_ID].name,
          }))
        : [],
    [dexFactories],
  );

  const onApply = useCallback(() => {
    vaultsFiltersActions.onSearch(input);

    vaultsFiltersActions.onUpdateTags(tags);
    vaultsFiltersActions.onUpdateRanges(ranges);
    vaultsFiltersActions.onUpdatePlatforms(platforms);
    vaultsFiltersActions.onUpdateSortBy(sort.sortBy);
    vaultsFiltersActions.onUpdateStrategy(strategy);
    vaultsFiltersActions.onUpdateV3Type(v3Type);
    vaultsFiltersActions.onUpdateFarmTypes(farmTypes);
    vaultsFiltersActions.onUpdateRiskLevel(riskLevel);
    vaultsFiltersActions.onUpdateProfitTokens(profitTokens);
    vaultsFiltersActions.onUpdateProfitPercents(profitLevel);
    vaultsFiltersActions.onUpdateSortDirection(sort.sortDirection);

    modalActions.hide();
  }, [
    tags,
    sort,
    input,
    ranges,
    v3Type,
    platforms,
    farmTypes,
    riskLevel,
    strategy,
    profitLevel,
    profitTokens,
  ]);

  useEffect(() => {
    setTags(initialTags);
    setRanges(initialRanges);
  }, [initialTags, initialRanges]);

  return {
    sort,
    input,
    strategy,
    farmTypes,
    riskLevel,
    profitLevel,
    tags,
    v3Type,
    ranges,
    profitTokens,
    profitTokensOptions,
    platforms,
    platformOptions,
    badges,
    disabledV3Type,
    isGlobalLoading,
    actions: {
      onTags,
      onRanges,
      onClear,
      onApply,
      onV3Type,
      onSortBy,
      onStrategy,
      onRiskLevel,
      onFarmTypes,
      onSetPlatform,
      onProfitLevel,
      onInputChange,
      onSortDirection,
      onSetProfitToken,
    },
  };
};

export default useAdvancedFilters;
